import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { Callout, H2, OL, P, Pull } from './ui';

const post: Post = { meta: postMeta('how-to-win-a-beat-battle'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Winning a beat battle is not the same skill as making a great album track. Battles compress
        a lot of judgment into 60 to 120 seconds played on phone speakers and judged by people who
        can listen to maybe 10 entries before they get fatigue. Production quality matters, but
        workflow and presentation matter just as much. Here are nine tactics that consistently move
        people up the leaderboard.
      </P>

      <H2 id="tactic-1">1. Lead with the loudest, cleanest sound first</H2>
      <P>
        First two seconds of your beat decide whether the listener leans in or scrolls. Front-load
        the most arresting element you have: a punchy kick + 808 with a vocal stab, or a clear
        melody hook. Nobody votes high for a beat that "gets good after the intro" because they skip
        past the intro.
      </P>

      <H2 id="tactic-2">2. Match your levels to the room</H2>
      <P>
        Not just LUFS. Match the perceived loudness of the top entries on the platform. If the
        average winning beat is sitting around -8 LUFS integrated and yours is at -14, yours sounds
        timid even if the mix is technically cleaner. Reference the leaderboard entries before
        exporting.
      </P>
      <Callout>
        Most online battle players listen on phone speakers or laptop speakers. Mix and master with
        that as the reality, not your studio monitors.
      </Callout>

      <H2 id="tactic-3">3. Use the kit creatively, do not fight it</H2>
      <P>
        On Prod Battle every producer in a match gets the same sample kit. The losers are the
        producers who try to bend the kit toward what they would normally use. The winners are the
        producers who pick the most interesting sound in the kit and build the beat around it. If
        the kit has a weird vocal one-shot, that is your hook. If it has a sub bass with character,
        that is your bassline.
      </P>
      <Pull>
        You are being scored on what you did with this kit, not what you would have done with your
        favorite kit.
      </Pull>

      <H2 id="tactic-4">4. Arrangement beats sound design at 90 seconds</H2>
      <P>
        At album-track length, sound design wins. At 60 to 120 seconds, arrangement wins. A great
        beat with three sections (intro, drop, breakdown back to drop) reads as a complete song. A
        loop with the same energy from 0 to 90 seconds reads as homework. Even a basic structure
        does this:
      </P>
      <OL>
        <li>0 to 8s: hook teaser, drums coming in</li>
        <li>8 to 32s: full drop with all elements</li>
        <li>32 to 48s: breakdown - drop the drums, keep the melody, build tension</li>
        <li>48 to 80s: full drop again, ideally with a new layer or fill</li>
        <li>80 to 90s: outro tail or hard stop</li>
      </OL>

      <H2 id="tactic-5">5. Test on a phone before submitting</H2>
      <P>
        Export your beat. Send it to your phone. Play it on the phone speaker. Does the kick and
        snare cut through? Is the 808 audible or does it disappear? If anything sounds muddy on a
        phone, fix it before you submit. The voters are listening on a phone.
      </P>

      <H2 id="tactic-6">6. Time management - leave 60 seconds for export</H2>
      <P>
        The most common loss in a beat battle is not getting your beat exported in time. Whatever
        the timer says, mentally subtract 60 seconds for bouncing, rendering, and uploading. If the
        round is 20 minutes, you have 19 minutes of production. Set a phone timer at the 19-minute
        mark and force yourself to bounce at that point even if you wanted to add one more thing. A
        submitted okay beat beats an unsubmitted great beat.
      </P>

      <H2 id="tactic-7">7. Voting psychology - distinctiveness wins</H2>
      <P>
        When voters listen to 10 entries in a row, the ones they remember are the ones that did
        something different from the others. If everyone in your match goes for a moody trap beat,
        the producer who goes for a clean melodic flip stands out. Distinctiveness compounds: if
        your beat is the only one with a particular hook, voters can describe it to themselves ("the
        one with the vocal chop") and that drives recall during ranking.
      </P>

      <H2 id="tactic-8">8. Mix the low end mono</H2>
      <P>
        Anything below 100 Hz should be mono. Stereo bass loses energy on small speakers and cancels
        on phone speakers held in landscape. Mono bass + stereo highs is the most bulletproof mix
        shape for battle entries.
      </P>

      <H2 id="tactic-9">9. What to do after you lose</H2>
      <P>
        You will lose battles. Often early. The producers who climb the leaderboard fastest are the
        ones who lose 20 battles in their first week and treat each one as a data point on what the
        audience actually responds to. Re-listen to the beat that beat you and try to articulate why
        it landed. That is your study material.
      </P>
      <Pull>The fastest path to top 10 is volume. Play 50 matches in your first month, not 5.</Pull>

      <H2 id="bonus">Bonus: be in the right genre for your skill</H2>
      <P>
        Prod Battle ranks you per genre. If you are a phonk producer trying to win in lo-fi house
        you are competing against the wrong opponents. Pick the genre where your strengths line up
        with what audiences expect, and stack wins there. You can always cross over later.
      </P>

      <H2 id="put-it-together">Put it together</H2>
      <P>Three quick steps:</P>
      <OL>
        <li>
          Open{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            /play
          </Link>{' '}
          and pick the genre you are strongest in.
        </li>
        <li>
          Run 5 matches back-to-back tonight. Use the same DAW template each time. The constraint
          forces you to focus on arrangement and creativity rather than sound design.
        </li>
        <li>
          The next morning, listen to every beat that beat yours. Note the one tactic each winner
          used. Apply two of them in your next session.
        </li>
      </OL>
      <P>
        Producers who do this for two weeks straight typically land top 50 in their genre. The ones
        who stay consistent for three months end up top 10.
      </P>
    </>
  );
}
